/**
 * Capture core: turns a raw observed request into a masked RequestEvent, applies
 * sampling, records it for the widget, and hands it to the transport. Interceptors
 * call `capture()`; everything sensitive is masked here before it leaves the SDK.
 */
import {
  maskHeaders,
  maskText,
  maskUrl,
  urlHasTokenInQuery,
  type Cause,
  type RequestEvent,
} from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';
import { Transport } from './transport.js';
import { store } from './store.js';
import { detectSource } from './source.js';
import { currentTraceId, type SpanIds } from './trace.js';
import { domainMatches, hostOf, isSameOrigin, matchesIgnore, pathOf, uuid } from './util.js';

export interface RawObservation {
  method: string;
  url: string;
  status: number | null;
  ok: boolean | null;
  durationMs: number;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBodySize?: number | null;
  responseBodySize?: number | null;
  bodyPreview?: string | null;
  error?: { type?: string; message?: string } | null;
  /** Stack captured at the call site (before async boundary). */
  stack?: string;
  span: SpanIds;
  /** Set when the request was blocked by the inline firewall (never actually sent). */
  blocked?: boolean;
}

export interface Policy {
  blocked: boolean;
  /** Why it was blocked: 'blocked-domain' | 'unknown-domain' | '' when allowed. */
  reason: string;
}

export class Runtime {
  transport: Transport;
  /** Every URL the SDK itself talks to (event/metric/log ingest + widget actions).
   * Derived the same way signals.ts builds its endpoints, so they stay in sync. */
  private selfUrls: string[];
  constructor(public cfg: ResolvedConfig) {
    this.transport = new Transport(cfg);
    const base = cfg.collectorUrl.replace(/\/api\/events\/ingest\/?$/, '').replace(/\/$/, '');
    this.selfUrls = [
      cfg.collectorUrl,
      `${base}/api/metrics/ingest`,
      `${base}/api/logs/ingest`,
      `${base}/api/perf/ingest`,
      `${base}/api/profiles/ingest`,
      `${base}/api/rules/allowed-domains`,
    ];
  }

  shouldIgnore(url: string): boolean {
    if (this.selfUrls.some((self) => url.includes(self))) return true; // never observe ourselves
    return matchesIgnore(url, this.cfg.ignoreUrls);
  }

  /**
   * Inline firewall verdict for an outgoing request. Only enforces when `enforce` is
   * on (otherwise violations are merely flagged via tags). An explicit blocked-domain
   * match always wins; unknown-domain blocking is limited to cross-origin requests so a
   * strict allowlist never accidentally kills the app's own same-origin API calls.
   */
  enforce(url: string): Policy {
    if (!this.cfg.enforce) return { blocked: false, reason: '' };
    const host = hostOf(url);
    if (domainMatches(host, this.cfg.blockedDomains)) return { blocked: true, reason: 'blocked-domain' };
    if (this.cfg.blockUnknownDomains && !isSameOrigin(url) && !domainMatches(host, this.cfg.allowedDomains)) {
      return { blocked: true, reason: 'unknown-domain' };
    }
    return { blocked: false, reason: '' };
  }

  private resolveUserId(): string | null {
    const u = this.cfg.userId;
    if (typeof u === 'function') return u() ?? null;
    return u ?? null;
  }

  private sampled(hasError: boolean): boolean {
    if (hasError && this.cfg.alwaysSampleErrors) return true;
    return Math.random() < this.cfg.sampleRate;
  }

  capture(obs: RawObservation): RequestEvent {
    const host = hostOf(obs.url);
    const path = pathOf(obs.url);
    const outgoing = true; // browser SDK only sees outgoing requests

    const blocked = domainMatches(host, this.cfg.blockedDomains);
    const domainKnown = domainMatches(host, this.cfg.allowedDomains);
    const planned = domainKnown && !blocked;

    const { source, cause } = detectSource(obs.stack);
    const hasError = !!obs.error || (obs.status !== null && obs.status >= 400);

    const tags: Record<string, unknown> = { ...this.cfg.tags };
    if (urlHasTokenInQuery(obs.url)) tags.token_in_query = true;
    if (blocked) tags.blocked_domain = true;
    if (this.cfg.blockUnknownDomains && outgoing && !domainKnown) tags.unknown_domain = true;
    if (obs.blocked) tags.firewall_blocked = true;

    const ev: RequestEvent = {
      id: uuid(),
      project_id: this.cfg.projectId,
      environment: this.cfg.environment,
      direction: 'outgoing',
      origin: 'frontend',
      method: obs.method.toUpperCase(),
      url: maskUrl(obs.url, { maskPii: this.cfg.maskPii }),
      host,
      path,
      status: obs.status,
      ok: obs.ok,
      duration_ms: Math.round(obs.durationMs * 10) / 10,
      request_body_size: obs.requestBodySize ?? null,
      response_body_size: obs.responseBodySize ?? null,
      request_headers: maskHeaders(obs.requestHeaders, { maskPii: this.cfg.maskPii }),
      response_headers: maskHeaders(obs.responseHeaders, { maskPii: this.cfg.maskPii }),
      body_preview: this.cfg.captureBodyPreview ? maskText(obs.bodyPreview, { maskPii: this.cfg.maskPii }) : null,
      trace_id: obs.span.traceId ?? currentTraceId(),
      span_id: obs.span.spanId,
      parent_span_id: obs.span.parentSpanId,
      source,
      cause: cause as Cause,
      planned,
      domain_known: domainKnown,
      user_id: this.resolveUserId(),
      session_id: this.cfg.sessionId ?? null,
      error: obs.error ? { type: obs.error.type ?? 'Error', message: obs.error.message ?? null } : null,
      tags,
      timestamp: new Date().toISOString(),
    };

    // Always record for the widget; only sample what we ship to the collector.
    store.record(ev);
    if (this.sampled(hasError)) this.transport.enqueue(ev);
    return ev;
  }
}

let runtime: Runtime | null = null;
export function setRuntime(rt: Runtime): void {
  runtime = rt;
}
export function getRuntime(): Runtime | null {
  return runtime;
}
